import gql from 'graphql-tag';
import className from 'classnames/bind';
import { useEffect, useRef } from 'react';
import { Splide, SplideTrack } from 'react-splide-ts';
import { QuoteCarouselFragment } from '@graphqlTypes';
import { CarouselPagination, Container, RevealWrapper, SectionHeader } from '@components';
import Slide from './Slide';

import styles from './QuoteCarousel.module.scss';
import { useInView } from "motion/react"
import { useViewSize } from "@/providers/ViewSizeContext";
import { AnimVals } from '@/utils/animUtils';
const cx = className.bind(styles);

export default function QuoteCarousel({ items, headline, subhead }: QuoteCarouselFragment) {

  const rootEl = useRef<HTMLDivElement>(null);
  const showHeader = Boolean(headline || subhead);

  // --------------------- Splide

  const splideOptions = {
    type: 'slide',
    arrows: true,
    pagination: true,
    speed: 500,
    flickMaxPages: 1,
    flickPower: 250,
    updateOnMove: true,

    // Desktop first (Splide default)
    perPage: 3,
    perMove: 3,
    gap: '3rem',
    padding: {
      left: '5rem',
      right: '5rem',
    },

    // Responsive options (smaller than desktop)
    breakpoints: {
      // Below 1025px
      1025: {
        perPage: 2,
        perMove: 2,
        gap: '3rem',
        padding: {
          left: '4rem',
          right: '4rem',
        },
        arrows: false
      },
      // Below 769px
      769: {
        perPage: 1,
        perMove: 1,
        fixedWidth: '76%',
        gap: '2rem',
        padding: {
          left: '2rem',
          right: '2rem',
        },
      },
    }
  }

  // Set specific accessibility attribute overrides dictated by the client
  useEffect(() => {
    if (!rootEl.current) return;
    const lists = rootEl.current.querySelectorAll('.splide__list');
    lists.forEach(list => {
      list.setAttribute('role', 'list');
    });
    const slides = rootEl.current.querySelectorAll('.splide__slide');
    slides.forEach(slide => {
      slide.setAttribute('role', 'listitem');
    });
  }, []);

  // -------------------- Animation

  const { isDesktop } = useViewSize();
  const REVEAL = AnimVals;
  const triggerPercent = isDesktop ? REVEAL.lg.triggerPercent : REVEAL.sm.triggerPercent;
  const isInView = useInView(rootEl, { amount: triggerPercent, once: REVEAL.once });

  // -------------------- Render

  return (
    <section className={cx(['quote-carousel'])} ref={rootEl}>
      <RevealWrapper isInView={isInView}>
        {showHeader && (
          <Container>
            <SectionHeader
              headline={headline}
              subhead={subhead}
              isInView={isInView}
            />
          </Container>
        )}
        <div className={cx('splide-container')}>
          <Splide className={cx('splide')}
            options={splideOptions}
            hasTrack={false}
          >
            <SplideTrack>
              {items.map((slide, index) => {
                return <Slide slide={slide} key={index} />;
              })}
            </SplideTrack>
            <Container>
              <CarouselPagination className={cx('story-carousel-bottom')} />
            </Container>
          </Splide>
        </div>
      </RevealWrapper>
    </section>
  )
}

QuoteCarousel.fragments = {
  entry: gql`
    fragment QuoteCarousel on FlexiblePageContentContentQuoteCarouselLayout {
      headline
      subhead
      items {
        quote
        attribution {
          name
          title
        }
        image {
          ...ACFMedia
        }
      }
    }
  `,
};
